/* The guided character builder: the nine steps of the Character Creation page, each
   with its picker, the rules behind it and a live copy of the sheet that lights up
   the area the step fills in. */

import { ComponentType, CSSProperties, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { CREATION_STEPS, SheetSection, SIDECAR } from './content/sheetText';
import rulesSteps from './content/steps';
import type { Catalog } from './catalog';
import { Character, missingParts, missingSteps, stepDone } from './model';
import CharacterDocument from './CharacterDocument';
import Dialog from './Dialog';
import { ShowNeeded, StepProps } from './BuilderParts';
import { ClassStep, DomainStep, HeritageStep } from './StepsChoose';
import { EquipmentStep, InfoStep, TraitsStep } from './StepsNumbers';
import { BackgroundStep, ConnectionStep, ExperienceStep } from './StepsStory';
import './Builder.css';

/** The picker of each creation step, in order; the sheet view's gears reuse them. */
export const STEP_BODIES: Array<ComponentType<StepProps>> = [
  ClassStep, HeritageStep, TraitsStep, InfoStep, EquipmentStep, BackgroundStep, ExperienceStep, DomainStep, ConnectionStep,
];

/** Each step's title cut to its subject, for the progress track and the Next button. */
const SHORT_TITLES = ['Class', 'Heritage', 'Traits', 'Information', 'Equipment', 'Background', 'Experiences', 'Domain Cards', 'Connections'];

/** The creation step a sheet area's gear opens. */
export const EDIT_STEP: Partial<Record<SheetSection, number>> = {
  class: 1, heritage: 2, traits: 3, evasion: 5, damage: 5, weapons: 5, armor: 5, inventory: 5,
  identity: 6, description: 6, background: 6, experience: 7, domainCards: 8, connections: 9,
};

interface Props {
  character: Character;
  catalog: Catalog;
  onChange: (c: Character) => void;
  onFinish: () => void;
}

export default function Builder({ character: c, catalog, onChange, onFinish }: Props) {
  const [step, setStep] = useState(c.progress?.step ?? 1);
  const [showSheet, setShowSheet] = useState(false);
  const [closeUp, setCloseUp] = useState(true);
  const [confirming, setConfirming] = useState(false);
  // After Keep building, missing steps are marked on the track and needed panels light up
  const [checking, setChecking] = useState(false);
  const meta = CREATION_STEPS[step - 1];
  const rules = rulesSteps[step - 1];
  const missing = missingSteps(c);
  const Body = STEP_BODIES[step - 1];
  const tips = meta.sections.filter((s) => SIDECAR[s]);
  const [lead, ...moreGuide] = meta.text;

  const go = (n: number) => {
    setStep(n);
    if (!c.finished) onChange({ ...c, progress: { mode: 'build', step: n } });
    document.querySelector('.content-area')?.scrollTo?.({ top: 0 });
  };

  const keepBuilding = () => {
    setConfirming(false);
    setChecking(true);
    go(missing[0]);
  };

  return (
    <div className="cb">
      <nav className="cb-stepper" aria-label="Creation steps" style={{ '--progress': (step - 1) / (CREATION_STEPS.length - 1) } as CSSProperties}>
        <p className="cb-stepper__count">
          <span>Step {step} of {CREATION_STEPS.length}</span>
          <span className="cb-stepper__now">{SHORT_TITLES[step - 1]}</span>
        </p>
        <ol className="cb-stepper__steps">
          {CREATION_STEPS.map((s, i) => (
            <li key={s.title}>
              <button
                type="button"
                className={['cb-stepper__step', step === i + 1 && 'is-current', stepDone(i + 1, c) && 'cb-stepper__step--done', checking && missing.includes(i + 1) && 'cb-stepper__step--needed'].filter(Boolean).join(' ')}
                aria-label={s.title}
                aria-current={step === i + 1 ? 'step' : undefined}
                onClick={() => go(i + 1)}
              >
                <span className="cb-stepper__num"><span>{i + 1}</span></span>
                <span className="cb-stepper__title">{SHORT_TITLES[i]}</span>
              </button>
            </li>
          ))}
        </ol>
      </nav>

      <ShowNeeded.Provider value={checking}>
        <div className={`cb-layout${showSheet ? ' is-sheet-open' : ''}`}>
          <section className="cb-step" aria-labelledby="cb-step-title">
            <h2 id="cb-step-title" className="cb-step__title">{meta.title}</h2>
            <p className="cb-step__lead">{lead}</p>
            {(moreGuide.length > 0 || rules) && (
              <details className="cb-rules">
                <summary>Rules for this step</summary>
                {moreGuide.map((p) => <p key={p}>{p}</p>)}
                {rules?.description}
                {rules?.details}
              </details>
            )}

            <Body c={c} catalog={catalog} set={(patch) => onChange({ ...c, ...patch })} />

            <footer className="cb-step__nav">
              <button type="button" className="cb-btn" disabled={step === 1} onClick={() => go(step - 1)}>
                <ChevronLeft size={16} aria-hidden="true" />Back
              </button>
              <button type="button" className="cb-btn cb-sheet-toggle" onClick={() => setShowSheet((s) => !s)}>
                {showSheet ? 'Hide my sheet' : 'Show my sheet'}
              </button>
              {step < CREATION_STEPS.length ? (
                <button type="button" className="cb-btn cb-btn--primary" onClick={() => go(step + 1)}>
                  Next: {SHORT_TITLES[step]}<ChevronRight size={16} aria-hidden="true" />
                </button>
              ) : (
                <button type="button" className="cb-btn cb-btn--primary" onClick={() => (missing.length ? setConfirming(true) : onFinish())}>
                  {c.finished ? 'Save character' : 'Create character'}
                </button>
              )}
            </footer>
            {step === CREATION_STEPS.length && missing.length > 0 && (
              <div className="cb-missing">
                <p>Finish these steps to complete your character:</p>
                {missing.map((n) => (
                  <button key={n} type="button" className="cb-chip" onClick={() => go(n)}>
                    {n}. {CREATION_STEPS[n - 1].title}
                  </button>
                ))}
              </div>
            )}
          </section>

          {confirming && <FinishConfirm character={c} onKeep={keepBuilding} onFinish={onFinish} />}

          <aside className="cb-preview" aria-label="Your sheet so far">
            <div className="cb-preview__bar">
              <p className="cb-preview__label">On your sheet</p>
              <div className="cb-switch" role="group" aria-label="Sheet view">
                <button type="button" className="back-btn" aria-pressed={closeUp} onClick={() => setCloseUp(true)}>Close-up</button>
                <button type="button" className="back-btn" aria-pressed={!closeUp} onClick={() => setCloseUp(false)}>Whole page</button>
              </div>
              <button type="button" className="back-btn cb-preview__close" aria-label="Close the sheet" onClick={() => setShowSheet(false)}>
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            <div className="cb-preview__page">
              {closeUp ? (
                // One close-up per lit area, so areas far apart on the page each show large; only the first is read out
                meta.sections.map((s, i) => (
                  <div key={s} aria-hidden={i > 0 || undefined}>
                    <CharacterDocument character={c} catalog={catalog} pages={[meta.page]} highlight={[s]} closeUp />
                  </div>
                ))
              ) : (
                <CharacterDocument character={c} catalog={catalog} pages={[meta.page]} highlight={meta.sections} />
              )}
            </div>
            <div className="cb-preview__tip">
              {tips.map((s) => <p key={s}>{SIDECAR[s]}</p>)}
              {tips.length === 0 && <p>The highlighted area of your {meta.page === 'guide' ? 'character guide' : 'card page'} fills in as you choose.</p>}
            </div>
          </aside>
        </div>
      </ShowNeeded.Provider>
    </div>
  );
}

/** Asks before finishing a character with creation steps missing, naming what each still needs. */
export function FinishConfirm({ character, onKeep, onFinish }: { character: Character; onKeep: () => void; onFinish: () => void }) {
  return (
    <Dialog
      role="alertdialog"
      title="Finish with missing information?"
      onClose={onKeep}
      footer={
        <>
          <button type="button" className="cb-btn" onClick={onKeep}>Keep building</button>
          <button type="button" className="cb-btn cb-btn--primary" onClick={onFinish}>Yes, finish anyway</button>
        </>
      }
    >
      <p>These steps are not finished yet:</p>
      <ul className="cb-problems">
        {missingSteps(character).map((n) => <li key={n}>{n}. {CREATION_STEPS[n - 1].title}: {missingParts(n, character).join(', ')}</li>)}
      </ul>
      <p className="cb-hint">Keep building to see them highlighted, or finish now and fill them in later: a warning icon on your sheet lists them.</p>
    </Dialog>
  );
}
